import { useCallback, useEffect, useRef, useState } from 'react';
import resolveApiBase from '../../tradingCockpit/apiBase';
import { todayIstDateString } from './useHistoricalTrades';

const API_BASE = resolveApiBase();
const API_KEY = process.env.REACT_APP_API_KEY;

const resolveUrl = (path) => {
    if (!API_BASE) return path;
    const base = API_BASE.endsWith('/') ? API_BASE.slice(0, -1) : API_BASE;
    return `${base}${path.startsWith('/') ? path : `/${path}`}`;
};

const EMPTY_TOTALS = {
    netPnl: 0,
    grossPnl: null,
    tcaCost: null,
    tradeCount: 0,
    tradingDays: 0,
    winDays: 0,
    lossDays: 0,
};

export function useDailyPnlSummary({
    strategyId,
    from,
    to,
    universe = null,
    enabled = true,
}) {
    const [days, setDays] = useState({});
    const [totals, setTotals] = useState(EMPTY_TOTALS);
    const [fromDate, setFromDate] = useState(from);
    const [toDate, setToDate] = useState(to);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const aliveRef = useRef(true);

    const fetchSummary = useCallback(async () => {
        if (!enabled || !strategyId) return;
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (from) params.set('from', from);
            if (to) params.set('to', to);
            if (universe && universe !== 'ALL') params.set('universe', universe);
            const path =
                strategyId === 'vyasa'
                    ? `/api/strategies/vyasa/history/daily?${params}`
                    : `/api/strategies/${strategyId}/history/daily?${params}`;
            const headers = { 'Content-Type': 'application/json' };
            if (API_KEY) headers['x-api-key'] = API_KEY;
            const resp = await fetch(resolveUrl(path), {
                method: 'GET',
                headers,
                credentials: 'include',
                cache: 'no-store',
            });
            if (!resp.ok) {
                throw new Error(`HTTP ${resp.status}`);
            }
            const data = await resp.json();
            if (!aliveRef.current) return;
            setDays(data.days && typeof data.days === 'object' ? data.days : {});
            setTotals({ ...EMPTY_TOTALS, ...(data.totals || {}) });
            setFromDate(data.fromDate || from);
            setToDate(data.toDate || to);
            setError(null);
        } catch (err) {
            if (!aliveRef.current) return;
            setDays({});
            setTotals(EMPTY_TOTALS);
            setError(err.message || String(err));
        } finally {
            if (aliveRef.current) setLoading(false);
        }
    }, [enabled, strategyId, from, to, universe]);

    useEffect(() => {
        aliveRef.current = true;
        fetchSummary();
        return () => {
            aliveRef.current = false;
        };
    }, [fetchSummary]);

    return {
        days,
        totals,
        fromDate,
        toDate,
        loading,
        error,
        refresh: fetchSummary,
    };
}

export function defaultOverviewFromDate(monthsBack = 7) {
    const today = todayIstDateString();
    const [year, month, day] = today.split('-').map(Number);
    const dt = new Date(year, month - 1, day);
    dt.setMonth(dt.getMonth() - monthsBack);
    return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
}

export default useDailyPnlSummary;
